/**
 * Extract URL search parameters from the current window location
 * @param {string} paramName - The name of the parameter to extract
 * @returns {string|null} The parameter value or null if not found
 */
export const getUrlParam = paramName => {
  const params = new URLSearchParams(window.location.search);
  return params.get(paramName);
};

/**
 * Extract multiple URL parameters at once
 * @param {string[]} paramNames - Array of parameter names to extract
 * @returns {Object} Object with parameter names as keys and their values (or null)
 */
export const getUrlParams = paramNames => {
  const params = new URLSearchParams(window.location.search);
  const result = {};

  paramNames.forEach(paramName => {
    result[paramName] = params.get(paramName);
  });

  return result;
};

/**
 * Extract Meta Pixel tracking parameters from cookies and URL
 * - fbp: Facebook Browser Pixel ID (from _fbp cookie, set by Meta Pixel JS)
 * - fbc: Facebook Click ID (from _fbc cookie or constructed from fbclid URL param)
 * @returns {Object} Object containing fbp and fbc values if available
 */
export const getMetaPixelParams = () => {
  // Get fbp from _fbp cookie (set by Meta Pixel JavaScript)
  const fbp = getCookieValue("_fbp");

  // Get fbc from _fbc cookie first, fallback to fbclid URL param
  let fbc = getCookieValue("_fbc");

  // If no _fbc cookie, check for fbclid in URL and construct fbc
  if (!fbc) {
    const params = new URLSearchParams(window.location.search);
    const fbclid = params.get("fbclid");
    if (fbclid) {
      // Format: fb.{subdomain_index}.{creation_time}.{fbclid}
      // Subdomain index is typically 1 for single-domain sites
      const timestamp = Math.floor(Date.now() / 1000);
      fbc = `fb.1.${timestamp}.${fbclid}`;
    }
  }

  return {
    fbp: fbp || null,
    fbc: fbc || null,
  };
};

/**
 * Helper function to get a cookie value by name (supports non-JSON cookies like _fbp, _fbc)
 * @param {string} name - Cookie name
 * @returns {string|null} Cookie value or null if not found
 */
const getCookieValue = name => {
  const match = document.cookie.match(new RegExp(`(^| )${name}=([^;]+)`));
  return match ? match[2] : null;
};
